import {
    Dialog,
    DialogContent,
    DialogProps,
    DialogTitle,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RootState } from '../../store';
import { hideNumberSelectionDialog, setActiveCustomerNumber } from '../../store/customer/actions';
import { CustomerState } from '../../store/customer/types';
import { CustomerNumber } from '../../types';
import { calculateCreditRemaining } from '../../utils/customer-number';
import CloseIcon from '../common/Icons/Close';
import UserAvatar from '../common/UserAvatar';
import { useCall } from '../../customer-call-sdk';

const StyledNumberSelectionDialog = styled(Dialog)`
    .dialog-paper {
        width: 550px;
        padding: 1rem;
        border-radius: 0.75rem;
        outline: none;
    }
    .dialog-paper:focus,
    .dialog-paper:focus-visible,
    .MuiDialog-container:focus,
    .MuiDialog-container:focus-visible {
        outline: none;
    }
    .dialog-title {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .title {
        font-size: 1.5rem;
        margin-bottom: 0;
        font-weight: bold;
    }
    .close-icon {
        width: 36px;
        height: 36px;
        cursor: pointer;
        position: absolute;
        top: 16px;
        right: 16px;
    }
    .list-item-row {
        margin-bottom: 0.75rem;
        padding: 0;
    }
    .list-item {
        background-color: #fff;
        border-radius: 1rem;
    }
    .list-item.selected,
    .list-item.Mui-selected,
    .list-item:hover,
    .list-item.Mui-selected:hover {
        background-color: #fff;
        box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.15);
        border-radius: 1rem;
    }
    .avatar-container {
    }
    .avatar {
        width: 36px;
        height: 36px;
    }
    .avatar svg {
        width: 28px;
        height: 28px;
    }
    .list-item-primary {
        font-size: 1.5rem;
        color: #111111;
        font-weight: 600;
    }
    .list-item-secondary {
        font-size: 1rem;
        color: rgba(17, 17, 17, 0.6);
        font-weight: 600;
    }
    .check-icon {
        width: 30px;
        height: 30px;
    }
`;
export interface NumberSelectionDialogProps extends DialogProps {}

const NumberSelectionDialog: React.FC<NumberSelectionDialogProps> = ({ onClose, ...props }) => {
    const dispatch = useDispatch();
    const { customerNumbers, activeCustomerNumber } = useSelector<RootState, CustomerState>(
        (state) => state.customerDetail,
    );
    const call = useCall();

    const handleNumberSelection = (customerNumber: CustomerNumber) => {
        call.setActivePhoneNumber(customerNumber.number);
        dispatch(setActiveCustomerNumber(customerNumber));
        dispatch(hideNumberSelectionDialog());
    };

    const getCustomerNumberText = (customerNumber: CustomerNumber) => {
        return customerNumber.number;
    };

    const getCustomerCreditText = (customerNumber: CustomerNumber) => {
        switch (customerNumber.creditType) {
            case 'Guest':
                return 'Guest - Free';
            case 'Credit-Group':
                return `${customerNumber.numberTitle || 'Work'} - ${calculateCreditRemaining(
                    customerNumber,
                )} minutes`;
            case 'Credit-Plan':
            default:
                return `${customerNumber.numberTitle || 'Personal'}  - ${calculateCreditRemaining(
                    customerNumber,
                )} minutes`;
        }
    };

    return (
        <StyledNumberSelectionDialog
            {...props}
            slotProps={{
                paper: {
                    className: 'dialog-paper',
                },
            }}
            onClose={onClose}
        >
            <DialogTitle>
                <div className="dialog-title" role="button">
                    <h3 className="title">Which account would you like to use?</h3>
                    <CloseIcon
                        className="close-icon"
                        onClick={onClose as any}
                        aria-label="Close Account Picker Dialog"
                    />
                </div>
            </DialogTitle>
            <DialogContent>
                <List aria-label="Toggle Phone Number or Account">
                    {customerNumbers.map((customerNumber) => {
                        const isSelected =
                            customerNumber.number === activeCustomerNumber?.number;
                        return (
                            <ListItem
                                key={customerNumber.number}
                                disablePadding
                                className="list-item-row"
                                secondaryAction={
                                    isSelected ? (
                                        <CheckCircleIcon className="check-icon" />
                                    ) : null
                                }
                            >
                                <ListItemButton
                                    className="list-item"
                                    classes={{ selected: 'selected' }}
                                    onClick={() => handleNumberSelection(customerNumber)}
                                    selected={isSelected}
                                >
                                    <ListItemAvatar className="avatar-container">
                                        <UserAvatar className="avatar" />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={getCustomerNumberText(customerNumber)}
                                        secondary={getCustomerCreditText(customerNumber)}
                                        slotProps={{
                                            primary: { className: 'list-item-primary' },
                                            secondary: { className: 'list-item-secondary' },
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>
        </StyledNumberSelectionDialog>
    );
};

export default NumberSelectionDialog;
